/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import {
  Divider,
  Row,
  Col,
  Typography,
  Tooltip,
} from 'antd';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import colors from '../../constants/Colors';
import WorkSlot from './WorkSlot';
import EditButton from '../../common/buttons/EditButton';
import { roundTotalRuntime } from '../../helpers/time';
import { getPendingTaskText, msToString } from '../timecard.helpers';
import { sortByStartTime } from '../../helpers/tasks';
import TimeCardApprovalButton, { APPROVE_TIMECARD_SUBMISSION, REJECT_TIMECARD_SUBMISSION } from '../TimeCardApprovalButton';

const { Text } = Typography;

const parseEntries = (day = {}, onCardLinkClick) => {
  const {
    dayTasks = [],
  } = day;
  if (dayTasks.length === 0) {
    return (
      <Row justify="space-around" type="flex">
        {/* TODO : make this look nice */}
        <i>No entries</i>
      </Row>
    );
  }
  let canEditCards = false;
  const views = [];
  const seen = new Set();
  const uniqueTasks = dayTasks.filter((task) => {
    if (task.id && seen.has(task.id)) {
      return false;
    }
    seen.add(task.id);
    return true;
  });
  uniqueTasks.sort(sortByStartTime);
  uniqueTasks.forEach((task) => {
    if (task.canEdit) canEditCards = true;
    let toolTipText = '';
    if (task.isPending) {
      toolTipText = getPendingTaskText({
        formUserName: task.formUserName,
        formCreatedAt: task.formCreatedAt,
        formName: task.formName,
        formNumber: task.formNumber,
      });
    }
    views.push(
      <Tooltip title={toolTipText} key={task.id}>
        <div
          key={task.id}
          style={{
            width: '95%',
            margin: 'auto',
          }}
        >
          <WorkSlot task={task} onCardLinkClick={onCardLinkClick} />
          <div style={{ height: '0.5rem' }} />
        </div>
      </Tooltip>,
    );
  });
  return { views, canEditCards };
};

function Scroller({
  day,
  dayHoursBreakdown,
  onEditClicked,
  roundingType,
  roundingInterval,
  roundingSetting,
  isApprovals,
  userId,
  showOtBreakdown,
  hasPaidFlag,
  onCardLinkClick,
}) {
  const titleStyle = {
    fontSize: 10,
    width: '100%',
    fontFamily: 'raleway-semibold',
    color: colors.ONTRACCR_TITLE_BLACK,
  };
  const { views, canEditCards } = parseEntries(day, onCardLinkClick);
  return (
    <div style={{ width: '-webkit-fill-available' }}>
      <Row justify="space-around" type="flex">
        <Text
          style={{
            fontSize: showOtBreakdown ? 14 : 16,
            width: '100%',
            textAlign: 'center',
            fontFamily: 'raleway-semibold',
            color: colors.ONTRACCR_TITLE_BLACK,
          }}
        >
          {showOtBreakdown && `Regular: ${' '}`}
          {msToString(
            roundTotalRuntime(
              showOtBreakdown ? dayHoursBreakdown?.regularTime : day.total + (day.totalPTO || 0),
              roundingInterval,
              roundingType,
              roundingSetting,
            ),
          )}
        </Text>
        {showOtBreakdown && (
          <>
            <Text
              style={{
                fontSize: 14,
                width: '100%',
                textAlign: 'center',
                fontFamily: 'raleway-semibold',
                color: colors.ONTRACCR_TITLE_BLACK,
              }}
            >
              OT: {' '}
              {msToString(
                roundTotalRuntime(
                  dayHoursBreakdown?.overtime,
                  roundingInterval,
                  roundingType,
                  roundingSetting,
                ),
              )}
            </Text>

            <Text
              style={{
                fontSize: 14,
                width: '100%',
                textAlign: 'center',
                fontFamily: 'raleway-semibold',
                color: colors.ONTRACCR_TITLE_BLACK,
              }}
            >
              DT OT: {' '}
              {msToString(
                roundTotalRuntime(
                  dayHoursBreakdown?.doubleOT,
                  roundingInterval,
                  roundingType,
                  roundingSetting,
                ),
              )}
            </Text>
          </>
        )}
        {showOtBreakdown && hasPaidFlag && (
          <Text
            style={{
              fontSize: 14,
              width: '100%',
              textAlign: 'center',
              fontFamily: 'raleway-semibold',
              color: colors.ONTRACCR_TITLE_BLACK,
            }}
          >
            PTO: {' '}
            {msToString(
              dayHoursBreakdown?.pto,
            )}
          </Text>
        )}

      </Row>
      <Row justify="space-between">
        <Col span={12}>
          <Row style={{ marginBottom: '0.2rem', marginLeft: '0.2rem' }}>
            <Text style={titleStyle}>
              {day?.minTS?.dt?.toLocaleString?.(DateTime.TIME_SIMPLE) ?? '--'}
            </Text>
          </Row>
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Row style={{ marginBottom: '0.2rem', marginRight: '0.2rem' }}>
            <Text style={titleStyle}>
              {day?.maxTS?.dt?.toLocaleString?.(DateTime.TIME_SIMPLE) ?? '--'}
            </Text>
          </Row>
        </Col>
      </Row>
      <Divider
        style={{
          backgroundColor: colors.ONTRACCR_BLACK,
          height: 1,
          marginTop: 0,
          marginBottom: 0,
          paddingBottom: 0,
        }}
      />
      <div
        style={{
          overflowY: 'auto',
          height: '9.866rem',
        }}
      >
        <div style={{ height: '0.5rem' }} />
        {views}
      </div>
      <Divider
        style={{
          backgroundColor: colors.ONTRACCR_BLACK,
          height: 1,
          marginTop: 0,
          marginBottom: 0,
          paddingBottom: 0,
        }}
      />
      {isApprovals && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <TimeCardApprovalButton
            record={day}
            type={APPROVE_TIMECARD_SUBMISSION}
            userId={userId}
          />
          <TimeCardApprovalButton
            record={day}
            type={REJECT_TIMECARD_SUBMISSION}
            userId={userId}
          />
        </div>
      )}
      {canEditCards && (
        <Row justify="space-around" type="flex">
          <EditButton
            editable
            onClick={(e) => {
              e.stopPropagation();
              onEditClicked({
                day,
              });
            }}
            customStyle={{ border: 'none' }}
          />
        </Row>
      )}
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
Scroller.propTypes = {
  day: PropTypes.object.isRequired,
  onEditClicked: PropTypes.func.isRequired,
  roundingType: PropTypes.string,
  roundingInterval: PropTypes.number.isRequired,
  roundingSetting: PropTypes.string,
  isApprovals: PropTypes.bool,
  userId: PropTypes.string.isRequired,
  dayHoursBreakdown: PropTypes.object,
  showOtBreakdown: PropTypes.bool,
  hasPaidFlag: PropTypes.bool,
  onCardLinkClick: PropTypes.func,
};

Scroller.defaultProps = {
  roundingType: null,
  isApprovals: false,
  roundingSetting: undefined,
  dayHoursBreakdown: {},
  showOtBreakdown: false,
  hasPaidFlag: false,
  onCardLinkClick: undefined,
};

export default Scroller;
